import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from 'antd'
import { useLocation } from 'react-router-dom'

import SearchSVG from '@/images/home/Search.svg?react'
import SButton from '@/components/common/SButton'
import TranslationNS from '@/utils/Constants/TranslationNS'
import useDebounce from '@/hooks/useDebounce'
import { useHomeSearchParams } from '@/utils/homeSearch'
import { openAdvancedSearch } from '@/utils/advancedSearchStore'

import './index.css'

const NavSearch = ({ showButton = true, isMobile = false, isLoggedIn }) => {
    const { t } = useTranslation(TranslationNS.HOME_PAGE)
    const [searchParams, setSearchParams] = useHomeSearchParams()

    const urlTerm = searchParams.get('q') || ''
    const [text, setText] = useState(urlTerm)
    const { pathname } = useLocation()

    // Mock: focusing the nav search reveals the advanced-search panel under the filter bar.
    // The panel lives on the homepage, so only open it there (elsewhere Enter navigates home).
    const revealAdvancedSearch = () => {
        if (pathname === '/') openAdvancedSearch()
    }

    // Keep the input in sync with the URL on back/forward (or external resets),
    // without fighting the user while they type (trimmed values are equal).
    useEffect(() => {
        setText((previous) => (previous.trim() === urlTerm ? previous : urlTerm))
    }, [urlTerm])

    const pushTermToUrl = (replace = true) => {
        const trimmed = text.trim()
        if (trimmed === urlTerm) return
        setSearchParams(
            (previous) => {
                if (trimmed) previous.set('q', trimmed)
                else previous.delete('q')
                return previous
            },
            { replace },
        )
    }

    // Live, debounced apply (~500ms). Runs on mount too, but no-ops because
    // text is seeded from the URL.
    useDebounce(text, () => pushTermToUrl(true), 500)

    return (
        <>
            {/* Desktop geometry follows the kit's .nav-search: 340px cap, 150px floor,
                    28px off the logo. The "Search" button is a sibling so it never eats
                    into that width. */}
            <div
                data-tour="nav-search"
                className={`flex items-center ${
                    isMobile ? 'flex-[1_1_auto] min-w-0' : 'ml-7 flex-[1_1_340px] max-w-[340px] min-w-[150px]'
                }`}
            >
                <Input
                    // kit .nav-search-ico: 14px, --field-ph, on desktop AND mobile
                    prefix={<SearchSVG className="size-3.5 shrink-0 text-(--field-ph)" />}
                    placeholder={t('Search Event Name')}
                    value={text}
                    onChange={(event) => setText(event.target.value)}
                    onPressEnter={() => pushTermToUrl(false)}
                    onClick={revealAdvancedSearch}
                    onFocus={revealAdvancedSearch}
                    size="small"
                    className={
                        isMobile
                            ? `
                                    w-full h-9
                                    p-0
                                    bg-transparent
                                    border-0
                                    shadow-none
                                    inline-flex items-center gap-2.5

                                    [&_.ant-input-prefix]:m-0

                                    hover:border-0
                                    focus-within:border-0
                                    focus-within:shadow-none

                                    [&_.ant-input]:bg-transparent
                                    [&_.ant-input]:p-0
                                    [&_.ant-input]:text-[14px]
                                    [&_.ant-input]:text-[#272727]
                                    [&_.ant-input]:placeholder:text-[#dbdadf]
                                    [&_.ant-input]:placeholder:text-[14px]

                                    before:content-none
                                `
                            : `
                                    w-full h-[33px]
                                    pl-2.5 pr-5 py-0
                                    bg-[#f7f7f9]
                                    rounded-[5px]
                                    border border-[#dbdadf]
                                    shadow-none
                                    inline-flex items-center gap-2.5
                                    overflow-hidden

                                    hover:border-[#dbdadf]
                                    focus-within:border-[#dbdadf]
                                    focus-within:shadow-none

                                    [&_.ant-input]:bg-transparent
                                    [&_.ant-input]:p-0
                                    [&_.ant-input]:text-sm
                                    [&_.ant-input]:text-[#272727]
                                    [&_.ant-input]:placeholder:text-[#dbdadf]
                                    [&_.ant-input]:placeholder:text-sm

                                    [&_.ant-input-prefix]:m-0
                                    [&_.ant-input-prefix]:mr-0

                                    before:content-none
                                `
                    }
                />
            </div>
            {showButton && (
                <SButton
                    value={t('Search')}
                    onClick={() => pushTermToUrl(false)}
                    className={`
                        ml-3.5 h-[33px]! min-w-16 py-0! px-3! rounded-[5px]! font-normal! border bg-[#272727]! flex-none
                        max-wide:hidden
                    `}
                />
            )}
        </>
    )
}

export default NavSearch
